import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatSpan } from '@et/schema/date';
import type { Locale } from '@et/schema';
import { EvidenceGlyph } from '../lexicon/EvidenceGlyph.js';
import type { TimelinePin } from './types.js';

export interface TimelineListProps {
  readonly pins: readonly TimelinePin[];
  readonly locale?: Locale;
  readonly selectedId?: string | null;
  readonly onSelect?: (id: string | null) => void;
  readonly id?: string;
  readonly className?: string;
}

/**
 * O equivalente em lista **nao e uma alternativa degradada**.
 *
 * Mostra exactamente o mesmo conjunto, sob os mesmos filtros, com a mesma
 * informacao — incluindo a ligacao evento -> descoberta e a marca de datacao
 * contestada. E navegavel por teclado sem truques, porque e uma lista.
 */
export function TimelineList({
  pins, locale = 'en', selectedId = null, onSelect, id, className,
}: TimelineListProps) {
  const { t } = useTranslation();
  const ordered = [...pins].sort((a, b) => a.event[0] - b.event[0] || a.id.localeCompare(b.id));

  return (
    <ol id={id} className={clsx('et-tllist', className)} data-testid="timeline-list">
      {ordered.map((pin) => {
        // A etiqueta autorada e canonica; fora do ingles deriva-se (§16 §3).
        const dateText = (locale === 'en' ? pin.dateLabel : undefined) ?? formatSpan(
          { earliest: pin.event[0], latest: pin.event[1] }, locale);
        return (
          <li key={pin.id} data-pin={pin.id} data-category={pin.category}>
            <button
              type="button"
              className="et-tllist__row"
              aria-pressed={pin.id === selectedId}
              onClick={() => onSelect?.(pin.id === selectedId ? null : pin.id)}
            >
              {pin.evidenceType !== undefined && (
                <EvidenceGlyph type={pin.evidenceType} size={16} decorative />
              )}
              <span className="et-tllist__title">{pin.title}</span>
              <span className="et-tllist__date">{dateText}</span>
              {pin.disputed === true && (
                <span className="et-tllist__disputed">{t('timeline.disputedDating')}</span>
              )}
              {pin.discovery != null && (
                <span className="et-tllist__discovery">
                  {t('timeline.foundIn', {
                    date: formatSpan({ earliest: pin.discovery[0], latest: pin.discovery[1] }, locale),
                  })}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
